import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  IconButton,
  useTheme,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  TextField,
  Stack,
} from '@mui/material';
import {
  Add,
  Schedule,
  Pets,
  Phone,
  Star,
  CalendarToday,
  TrendingUp,
  MoreVert,
  Edit,
  Delete,
  Cancel,
  AccountBalanceWallet,
  Close,
  Message,
  PhotoCamera,
  Send,
  CheckCircle,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BookingStatusChip from '../components/BookingStatusChip';
import apiService from '../services/api';
import ServiceUpdatesChat from '../components/ServiceUpdatesChat';
import LiveTracking from '../components/LiveTracking';
import { SkeletonBlock, SkeletonCard } from '../components/Skeletons';

const OwnerDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingServices, setPendingServices] = useState([]);
  const [upcomingServices, setUpcomingServices] = useState([]);
  const [inProgressServices, setInProgressServices] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pets, setPets] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    recent_services: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [serviceDetailsOpen, setServiceDetailsOpen] = useState(false);
  const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);
  const [serviceUpdates, setServiceUpdates] = useState([]);
  const [manageServiceDialogOpen, setManageServiceDialogOpen] = useState(false);
  const [activeService, setActiveService] = useState(null);
  const [updateMessage, setUpdateMessage] = useState('');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    comment: ''
  });
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    // Auto-refresh service updates every 30 seconds when managing a service
    let interval;
    if (activeService && manageServiceDialogOpen) {
      fetchServiceUpdates(activeService.id);
      interval = setInterval(() => {
        fetchServiceUpdates(activeService.id);
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeService, manageServiceDialogOpen]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch owner dashboard data and pets
      const [dashboard, petsData] = await Promise.all([
        apiService.getOwnerDashboard(),
        apiService.getPets()
      ]);
      
      setDashboardData(dashboard);
      setPets(petsData);
      
      // Separate services by status from dashboard upcoming bookings
      const allServices = dashboard.upcoming_bookings || [];
      
      const pending = allServices.filter(service => service.status === 'pending');
      const accepted = allServices.filter(service => service.status === 'accepted');
      const inProgress = allServices.filter(service => service.status === 'in_progress');
      
      setPendingServices(pending);
      setUpcomingServices(accepted);
      setInProgressServices(inProgress);
      
      // Process completed services for recent activity from dashboard data
      const recentBookings = dashboard.recent_bookings || [];
      
      const recentCompletedServices = recentBookings
        .slice(0, 5) // Get last 5 completed services
        .map(service => {
          return {
            ...service, // Include all service data including review
            type: 'service_completed',
            handlerName: service.handler ? `${service.handler.first_name} ${service.handler.last_name}` : 'Handler',
            petNames: service.pets?.map(pet => pet.name) || service.pet_names || [],
            duration: service.duration_minutes || service.duration,
            date: service.scheduled_date,
            time: service.scheduled_time,
            completedAt: service.actual_end_time,
            handler_review: service.handler_review || null
          };
        });
      setRecentActivity(recentCompletedServices);
      
    } catch (err) {
      setError('Failed to load dashboard data. Please try again.');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, service) => {
    setAnchorEl(event.currentTarget);
    setSelectedService(service);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // Don't clear selectedService here - keep it for dialogs
  };

  const handleEditService = () => {
    handleMenuClose();
    navigate(`/book-service?edit=${selectedService.id}`);
    setSelectedService(null); // Clear after navigation
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };

  const handleCancelClick = () => {
    handleMenuClose();
    setCancelDialogOpen(true);
  };

  const handleDeleteService = async () => {
    if (!selectedService) return;
    
    try {
      setActionLoading(true);
      await apiService.deleteServiceBooking(selectedService.id);
      
      setDeleteDialogOpen(false);
      setSelectedService(null);
      await fetchDashboardData();
      
      setError('Service deleted successfully.');
    } catch (err) {
      setError('Failed to delete service. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelService = async () => {
    if (!selectedService) return;
    
    try {
      setActionLoading(true);
      const result = await apiService.cancelServiceBookingWithFee(selectedService.id);
      setCancelDialogOpen(false);
      setSelectedService(null);
      
      if (result.cancellation_fee > 0) {
        setError(`Service cancelled. Cancellation fee of R${result.cancellation_fee} will be charged.`);
      } else {
        setError('Service cancelled successfully.');
      }
      
      await fetchDashboardData(); // Refresh data
    } catch (err) {
      setError('Failed to cancel service. Please try again.');
      console.error('Error cancelling service:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatRating = (rating) => {
    if (!rating) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Star sx={{ fontSize: 16, color: 'gold' }} />
        <Typography variant="body2" sx={{ color: 'gold', fontWeight: 600 }}>
          {rating}/5
        </Typography>
      </Box>
    );
  };

  const formatOwnerReviewText = (service) => {
    if (!service.handler_review) return null;
    return `Your Review: ${service.handler_review.rating}/5`;
  };

  const renderPetPhotos = (pets, size = 56, borderColor = 'primary.main') => {
    if (!pets || pets.length === 0) return null;
    
    if (pets.length === 1) {
      return pets[0].photo ? (
        <Avatar 
          src={pets[0].photo}
          sx={{ 
            width: size, 
            height: size,
            border: 2,
            borderColor: borderColor
          }}
        >
          <Pets />
        </Avatar>
      ) : (
        <Avatar sx={{ bgcolor: theme.palette.info.main, width: size, height: size }}>
          <Pets />
        </Avatar>
      );
    }
    
    // Multiple pets - show first photo with count overlay
    const firstPet = pets[0];
    return (
      <Box sx={{ position: 'relative' }}>
        <Avatar 
          src={firstPet.photo}
          sx={{ 
            width: size, 
            height: size,
            border: 2,
            borderColor: borderColor
          }}
        >
          <Pets />
        </Avatar>
        {pets.length > 1 && (
          <Avatar 
            sx={{ 
              position: 'absolute',
              bottom: -4,
              right: -4,
              width: 24,
              height: 24,
              bgcolor: 'primary.main',
              fontSize: '0.75rem',
              fontWeight: 600
            }}
          >
            +{pets.length - 1}
          </Avatar>
        )}
      </Box>
    );
  };

  // Group services by category and status
  const groupServicesByCategoryAndStatus = (services) => {
    const groups = {};
    services.forEach(service => {
      let category = 'Other';
      if (typeof service.service_type === 'string') {
        category = service.service_category || 'Other';
      } else if (service.service_type) {
        category = service.service_type.category?.name || service.service_type.category_name || 'Other';
      }
      const status = service.status || 'other';
      if (!groups[category]) groups[category] = {};
      if (!groups[category][status]) groups[category][status] = [];
      groups[category][status].push(service);
    });
    return groups;
  };

  const handleViewServiceDetails = async (serviceId) => {
    try {
      setActionLoading(true);
      // Fetch service details and updates
      const [serviceDetails, updates] = await Promise.all([
        apiService.getServiceBooking(serviceId),
        apiService.getServiceUpdates(serviceId)
      ]);
      
      setSelectedServiceDetails(serviceDetails);
      setServiceUpdates(updates);
      setServiceDetailsOpen(true);
    } catch (err) {
      console.error('Error fetching service details:', err);
      setError('Failed to load service details.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseServiceDetails = () => {
    setServiceDetailsOpen(false);
    setSelectedServiceDetails(null);
    setServiceUpdates([]);
  };

  const handleManageService = (service) => {
    setActiveService(service);
    setManageServiceDialogOpen(true);
    handleMenuClose();
  };

  const handleSendMessage = async () => {
    if (!activeService || !updateMessage.trim()) return;
    
    try {
      setActionLoading(true);
      const updateData = {
        update_type: 'message',
        message: updateMessage,
      };
      
      await apiService.createServiceUpdate(activeService.id, updateData);
      await fetchServiceUpdates(activeService.id);
      
      setUpdateMessage('');
    } catch (err) {
      setError('Failed to send message.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendPhoto = async (file) => {
    if (!activeService || !file) return;
    try {
      setActionLoading(true);
      await apiService.createServiceUpdate(activeService.id, {
        update_type: 'photo',
        photo: file,
        message: '',
      });
      await fetchServiceUpdates(activeService.id);
    } catch (err) {
      setError('Failed to send photo.');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchServiceUpdates = async (serviceId) => {
    try {
      const updates = await apiService.getServiceUpdates(serviceId);
      setServiceUpdates(updates);
    } catch (err) {
      console.error('Error fetching service updates:', err);
    }
  };

  const handleOpenReviewDialog = async (service) => {
    // If the service doesn't have complete handler information, fetch the full details
    if (!service.handler?.id) {
      try {
        const fullServiceDetails = await apiService.getServiceBooking(service.id);
        setSelectedServiceDetails(fullServiceDetails);
      } catch (err) {
        console.error('Error fetching service details:', err);
        setError('Failed to load service details. Please try again.');
        return;
      }
    } else {
      setSelectedServiceDetails(service);
    }
    setReviewDialogOpen(true);
  };

  const handleCloseReviewDialog = () => {
    setReviewDialogOpen(false);
    setReviewData({ rating: 0, comment: '' });
  };

  const handleSubmitReview = async () => {
    if (!selectedServiceDetails || reviewData.rating === 0) {
      setError('Please provide a rating');
      return;
    }

    // Debug: Log the service details to see what we have
    console.log('Selected service details:', selectedServiceDetails);
    console.log('Handler info:', selectedServiceDetails.handler);

    // Check if we have handler information
    if (!selectedServiceDetails.handler?.id) {
      setError('Handler information not available. Please try again.');
      return;
    }

    try {
      setReviewLoading(true);
      const reviewPayload = {
        service: selectedServiceDetails.id,
        rating: reviewData.rating,
        punctuality_rating: reviewData.rating, // Use same rating for now
        communication_rating: reviewData.rating, // Use same rating for now
        care_rating: reviewData.rating, // Use same rating for now
        title: `Review for ${selectedServiceDetails.handler?.first_name || 'Handler'}`,
        comment: reviewData.comment,
        would_recommend: reviewData.rating >= 4,
        would_book_again: reviewData.rating >= 4
      };

      console.log('Review payload:', reviewPayload);

      await apiService.createHandlerReview(reviewPayload);
      
      // Refresh the service details to show the new review
      await handleViewServiceDetails(selectedServiceDetails.id);
      
      handleCloseReviewDialog();
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) {
    // Skeletons in the geometry of the dashboard, not a spinner on a blank page.
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <SkeletonBlock width={280} height={30} radius={8} sx={{ mb: 1 }} />
        <SkeletonBlock width={200} height={18} sx={{ mb: 4 }} />
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i} sx={{ flex: '1 1 180px', minHeight: 140 }} />
          ))}
        </Box>
        <SkeletonCard sx={{ minHeight: 260 }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header — option 2a: display greeting, plain sub-line. SA voice is
          for celebration/marketing register, and a greeting qualifies. */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: { xs: 26, md: 34 }, mb: 1 }}>
          Howzit {user?.first_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Here's what's happening with your pets today.
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Stats Cards */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <Card sx={{ textAlign: 'center', p: 2, flex: 1 }}>
            <CardContent>
              <Pets sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {dashboardData.stats?.total_pets || pets.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pets in your pack
              </Typography>
              {(dashboardData.stats?.total_pets || pets.length) === 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => navigate('/pets')}
                  sx={{ mt: 2, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                >
                  Add a pet
                </Button>
              )}
            </CardContent>
          </Card>
          <Card sx={{ textAlign: 'center', p: 2, flex: 1 }}>
            <CardContent>
              <Schedule sx={{ fontSize: 40, color: theme.palette.secondary.main, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {dashboardData.stats?.upcoming_services || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upcoming services
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ textAlign: 'center', p: 2, flex: 1 }}>
            <CardContent>
              <TrendingUp sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {dashboardData.stats?.total_services || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total services
              </Typography>
            </CardContent>
          </Card>
        </Stack>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<Add />}
            onClick={() => navigate('/book-service')}
            sx={{ px: 5, py: 1.5, width: { xs: '100%', sm: 'auto' } }}
          >
            Book a walk
          </Button>
        </Box>

        {/* Main Content and Sidebar */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
          {/* Main Content */}
          <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
            {/* Pending Services */}
            {pendingServices.length > 0 && (
              <Card sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Pending Services
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    These services are waiting for handler acceptance
                  </Typography>
                </Box>
                <List>
                  {pendingServices.map((service, index) => (
                    <React.Fragment key={service.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          {renderPetPhotos(service.pets, 56, 'warning.main')}
                        </ListItemAvatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Primary content */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {(() => {
                                if (!service.service_type) {
                                  return 'Service';
                                }
                                if (typeof service.service_type === 'string') {
                                  return service.service_type;
                                }
                                return service.service_type.name || 'Service';
                              })()} for {service.pets?.map(pet => pet.name).join(', ') || service.pet_names?.join(', ') || 'your pets'}
                            </Typography>
                            <BookingStatusChip status={service.status} />
                          </Box>
                          
                          {/* Secondary content */}
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                            📅 {formatDate(service.scheduled_date)} at {formatTime(service.scheduled_time)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                            ⏱️ {service.duration_minutes || service.duration} min • {service.service_type?.category_name || 'Service'}
                          </Typography>
                          <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, display: 'block' }}>
                            💰 R{service.total_price}
                          </Typography>
                          {service.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                              📝 "{service.notes}"
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton 
                            onClick={(e) => handleMenuOpen(e, service)}
                            size="small"
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </ListItem>
                      {index < pendingServices.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Card>
            )}

            {/* Upcoming Services */}
            <Card sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Upcoming Services
              </Typography>
              {upcomingServices.length > 0 ? (
                <>
                  {(() => {
                    const grouped = groupServicesByCategoryAndStatus(upcomingServices);
                    return Object.entries(grouped).map(([category, statuses]) => (
                      <Box key={category} sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.light', pb: 1 }}>
                          {category}
                        </Typography>
                        {Object.entries(statuses).map(([status, services]) => (
                          <Box key={status} sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>
                              {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                            </Typography>
                            <List>
                              {services.map((service, index) => (
                                <React.Fragment key={service.id}>
                                  <ListItem sx={{ px: 0 }}>
                                    <ListItemAvatar>
                                      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                        {service.handler?.first_name?.[0] || 'H'}
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={
                                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
                                            {(() => {
                                              if (!service.service_type) {
                                                return 'Service';
                                              }
                                              if (typeof service.service_type === 'string') {
                                                return service.service_type;
                                              }
                                              return service.service_type.name || 'Service';
                                            })()} for {service.pets?.map(pet => pet.name).join(', ') || service.pet_names?.join(', ') || 'your pets'} with {service.handler ? `${service.handler.first_name || ''} ${service.handler.last_name || ''}`.trim() || 'Handler' : 'Handler'}
                                            {service.handler?.average_rating && (
                                              <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Star sx={{ fontSize: 14, color: 'gold' }} />
                                                <Typography variant="caption" sx={{ color: 'gold', fontWeight: 600 }}>
                                                  {service.handler.average_rating}/5
                                                </Typography>
                                              </Box>
                                            )}
                                          </Typography>
                                          <BookingStatusChip status={service.status} />
                                        </Box>
                                      }
                                      secondary={
                                        <>
                                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <CalendarToday sx={{ fontSize: 16 }} />
                                              <Typography variant="body2" component="span">
                                                {service.scheduled_date} at {service.scheduled_time}
                                              </Typography>
                                            </Box>
                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Schedule sx={{ fontSize: 16 }} />
                                              <Typography variant="body2" component="span">{service.duration_minutes || service.duration} min</Typography>
                                            </Box>
                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <AccountBalanceWallet sx={{ fontSize: 16 }} />
                                              <Typography variant="body2" component="span">R{service.total_price}</Typography>
                                            </Box>
                                          </Box>
                                          <Box component="span" sx={{ 
                                            display: 'block', 
                                            mt: 1.5, 
                                            p: 1.5, 
                                            bgcolor: 'grey.50', 
                                            borderRadius: 1.5,
                                            border: '1px solid',
                                            borderColor: 'grey.200'
                                          }}>
                                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <Typography variant="caption" color="text.secondary" component="span" sx={{ fontWeight: 500 }}>
                                                💡 Cancellation policy: R{service.cancellation_fee || '10.00'} fee applies if cancelled after handler acceptance
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </>
                                      }
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <IconButton>
                                        <Phone />
                                      </IconButton>
                                      <IconButton 
                                        onClick={(e) => handleMenuOpen(e, service)}
                                        size="small"
                                      >
                                        <MoreVert />
                                      </IconButton>
                                    </Box>
                                  </ListItem>
                                  {index < services.length - 1 && <Divider />}
                                </React.Fragment>
                              ))}
                            </List>
                          </Box>
                        ))}
                      </Box>
                    ));
                  })()}
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Schedule sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    No upcoming services
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/book-service')}
                    sx={{ textTransform: 'none' }}
                  >
                    Book Your First Service
                  </Button>
                </Box>
              )}
            </Card>

            {/* In Progress Services */}
            {inProgressServices.length > 0 && (
              <Card sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  In Progress Services
                </Typography>
                <List>
                  {inProgressServices.map((service, index) => (
                    <React.Fragment key={service.id}>
                      <ListItem sx={{ px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {service.handler?.first_name?.[0] || 'H'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
                                {(() => {
                                  if (!service.service_type) {
                                    return 'Service';
                                  }
                                  if (typeof service.service_type === 'string') {
                                    return service.service_type;
                                  }
                                  return service.service_type.name || 'Service';
                                })()} for {service.pets?.map(pet => pet.name).join(', ') || service.pet_names?.join(', ') || 'your pets'} with {service.handler ? `${service.handler.first_name || ''} ${service.handler.last_name || ''}`.trim() || 'Handler' : 'Handler'}
                                {service.handler?.average_rating && (
                                  <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <Star sx={{ fontSize: 14, color: 'gold' }} />
                                    <Typography variant="caption" sx={{ color: 'gold', fontWeight: 600 }}>
                                      {service.handler.average_rating}/5
                                    </Typography>
                                  </Box>
                                )}
                              </Typography>
                              <Chip
                                label="In Progress"
                                size="small"
                                color="primary"
                                sx={{ 
                                  fontWeight: 600,
                                  '& .MuiChip-label': {
                                    px: 1.5,
                                    fontSize: '0.75rem'
                                  }
                                }}
                              />
                            </Box>
                          }
                          secondary={
                            <>
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarToday sx={{ fontSize: 16 }} />
                                <Typography variant="body2" component="span">
                                    {service.scheduled_date} at {service.scheduled_time}
                                </Typography>
                              </Box>
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Schedule sx={{ fontSize: 16 }} />
                                  <Typography variant="body2" component="span">{service.duration_minutes || service.duration} min</Typography>
                                </Box>
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <AccountBalanceWallet sx={{ fontSize: 16 }} />
                                  <Typography variant="body2" component="span">R{service.total_price}</Typography>
                                </Box>
                              </Box>
                            </>
                          }
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Message />}
                            onClick={() => handleManageService(service)}
                            sx={{ textTransform: 'none' }}
                          >
                            Manage Service
                          </Button>
                          <IconButton>
                            <Phone />
                          </IconButton>
                        </Box>
                      </ListItem>
                      {index < inProgressServices.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Card>
            )}
          </Stack>

          {/* Sidebar - Recent Activity */}
          <Card sx={{ p: 3, width: { xs: '100%', md: 350 } }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
              Recent Activity
            </Typography>
            {recentActivity.length > 0 ? (
              <List>
                {recentActivity.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem 
                      sx={{ px: 0, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                      onClick={() => handleViewServiceDetails(activity.id)}
                    >
                      <ListItemAvatar>
                        {renderPetPhotos(activity.pets, 56, 'success.main')}
                      </ListItemAvatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Primary content */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {activity.service_type?.name || 'Pet Service'}
                          </Typography>
                          {activity.handler_review && formatRating(activity.handler_review.rating)}
                        </Box>
                        
                        {/* Secondary content */}
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          👤 {activity.handlerName || 'Handler'}
                          {activity.handler?.average_rating && (
                            <Box component="span" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <Star sx={{ fontSize: 14, color: 'gold' }} />
                              <Typography variant="caption" sx={{ color: 'gold', fontWeight: 600 }}>
                                {activity.handler.average_rating}/5
                              </Typography>
                            </Box>
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          🐕 {activity.petNames?.join(', ') || 'Pets'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          📅 {formatDate(activity.date)} at {formatTime(activity.time)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          ⏱️ {activity.duration} min • {activity.service_type?.category_name || 'Service'}
                        </Typography>
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, display: 'block' }}>
                          💰 R{activity.total_price}
                        </Typography>
                        {formatOwnerReviewText(activity) && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            📝 {formatOwnerReviewText(activity)}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                        <Chip 
                          label="Completed" 
                          color="success" 
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            '& .MuiChip-label': {
                              px: 1.5,
                              fontSize: '0.75rem'
                            }
                          }}
                        />
                        {!activity.handler_review && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Star />}
                            onClick={async (e) => {
                              e.stopPropagation();
                              await handleOpenReviewDialog(activity);
                            }}
                            sx={{ 
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 1
                            }}
                          >
                            Review
                          </Button>
                        )}
                      </Box>
                    </ListItem>
                    {index < recentActivity.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Schedule sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No recent activity yet
                </Typography>
              </Box>
            )}
          </Card>
        </Stack>
      </Stack>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedService && selectedService.status === 'pending' && [
          <MenuItem key="edit" onClick={handleEditService}>
            <Edit sx={{ mr: 1 }} />
            Edit Service
          </MenuItem>,
          <MenuItem key="delete" onClick={handleDeleteClick}>
            <Delete sx={{ mr: 1 }} />
            Delete Service
          </MenuItem>
        ]}
        {selectedService && selectedService.status === 'accepted' && (
          <MenuItem onClick={handleCancelClick}>
            <Cancel sx={{ mr: 1 }} />
            Cancel Service (Fee applies)
          </MenuItem>
        )}
        {selectedService && selectedService.status === 'in_progress' && (
          <MenuItem onClick={() => handleManageService(selectedService)}>
            <Message sx={{ mr: 1 }} />
            Manage Service
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedService(null);
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Service
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this service? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedService(null);
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteService}
            color="error" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          setCancelDialogOpen(false);
          setSelectedService(null);
        }}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          Cancel Service
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            {selectedService && (
              <>
                Are you sure you want to cancel this service?
                <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="warning.dark">
                    <strong>Note:</strong> A cancellation fee of R{selectedService.cancellation_fee || '10.00'} 
                    will be charged since a handler has already accepted this service.
                  </Typography>
                </Box>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCancelDialogOpen(false);
              setSelectedService(null);
            }}
            disabled={actionLoading}
          >
            Keep Service
          </Button>
          <Button 
            onClick={handleCancelService} 
            color="warning" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Cancel Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Service Details Dialog */}
      <Dialog
        open={serviceDetailsOpen}
        onClose={handleCloseServiceDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Service Details</Typography>
            {selectedServiceDetails && (
              <Typography variant="subtitle2" color="text.secondary">
                {(() => {
                  if (!selectedServiceDetails.service_type) {
                    return 'Service';
                  }
                  if (typeof selectedServiceDetails.service_type === 'string') {
                    return selectedServiceDetails.service_type;
                  }
                  return selectedServiceDetails.service_type.name || 'Service';
                })()} for {selectedServiceDetails.pets?.map(pet => pet.name).join(', ') || selectedServiceDetails.pet_names?.join(', ') || 'your pets'} with {selectedServiceDetails.handler ? `${selectedServiceDetails.handler.first_name || ''} ${selectedServiceDetails.handler.last_name || ''}`.trim() || 'Handler' : 'Handler'}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseServiceDetails}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedServiceDetails && (
            <Grid container spacing={3}>
              {/* Service Summary */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Service Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                      <Typography variant="body1">{selectedServiceDetails.scheduled_date} at {selectedServiceDetails.scheduled_time}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">{selectedServiceDetails.duration} minutes</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <BookingStatusChip status={selectedServiceDetails.status} />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Pickup Location</Typography>
                      <Typography variant="body1">{selectedServiceDetails.pickup_address}</Typography>
                    </Grid>
                    {selectedServiceDetails.special_instructions && (
                      <Grid size={12}>
                        <Typography variant="body2" color="text.secondary">Special Instructions</Typography>
                        <Typography variant="body1">{selectedServiceDetails.special_instructions}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>

              {/* Pricing Details */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Pricing</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Base Price:</Typography>
                    <Typography variant="body2">R{selectedServiceDetails.base_price || selectedServiceDetails.total_price}</Typography>
                  </Box>
                  {selectedServiceDetails.additional_pet_fee && parseFloat(selectedServiceDetails.additional_pet_fee) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Additional Pet Fee:</Typography>
                      <Typography variant="body2">R{selectedServiceDetails.additional_pet_fee}</Typography>
                    </Box>
                  )}
                  {selectedServiceDetails.tip_amount && parseFloat(selectedServiceDetails.tip_amount) > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Tip:</Typography>
                      <Typography variant="body2" color="success.main">R{selectedServiceDetails.tip_amount}</Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Total:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>R{selectedServiceDetails.total_price}</Typography>
                  </Box>
                </Card>
              </Grid>

              {/* Review Details */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Your Review</Typography>
                  {selectedServiceDetails.handler_review ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        {formatRating(selectedServiceDetails.handler_review.rating)}
                      </Box>
                      {selectedServiceDetails.handler_review.comment && (
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                            "{selectedServiceDetails.handler_review.comment}"
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Reviewed on {new Date(selectedServiceDetails.handler_review.created_at).toLocaleDateString()}
                      </Typography>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No review yet
                      </Typography>
                      {!selectedServiceDetails.pet_reviews?.length && (
                        <Button
                          variant="contained"
                          onClick={() => handleOpenReviewDialog(selectedServiceDetails)}
                          startIcon={<Star />}
                          sx={{ textTransform: 'none' }}
                        >
                          Review Service
                        </Button>
                      )}
                      {selectedServiceDetails.pet_reviews?.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Handler has submitted pet reviews
                          </Typography>
                          <Star sx={{ fontSize: '1rem', color: 'warning.main' }} />
                        </Box>
                      )}
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Pet Reviews (if available) */}
              {selectedServiceDetails.pet_reviews && selectedServiceDetails.pet_reviews.length > 0 && (
                <Grid size={12}>
                  <Card sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Pet Reviews</Typography>
                    <Stack spacing={2}>
                      {selectedServiceDetails.pet_reviews.map((review, index) => (
                        <Box key={review.id || index} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            {review.pet?.photo && (
                              <Avatar 
                                src={review.pet.photo}
                                sx={{ width: 40, height: 40 }}
                              >
                                {review.pet.name?.[0] || 'P'}
                              </Avatar>
                            )}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {review.pet?.name || 'Pet'}
                            </Typography>
                            {formatRating(review.rating)}
                          </Box>
                          
                          {/* Review Notes */}
                          <Stack spacing={1} sx={{ mt: 2 }}>
                            {review.energy_level_notes && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Energy Level:
                                </Typography>
                                <Typography variant="body2">{review.energy_level_notes}</Typography>
                              </Box>
                            )}
                            {review.socialization_notes && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Socialization:
                                </Typography>
                                <Typography variant="body2">{review.socialization_notes}</Typography>
                              </Box>
                            )}
                            {review.obedience_notes && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Obedience:
                                </Typography>
                                <Typography variant="body2">{review.obedience_notes}</Typography>
                              </Box>
                            )}
                            {review.other_notes && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Other Notes:
                                </Typography>
                                <Typography variant="body2">{review.other_notes}</Typography>
                              </Box>
                            )}
                            {review.special_considerations && (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Special Considerations:
                                </Typography>
                                <Typography variant="body2">{review.special_considerations}</Typography>
                              </Box>
                            )}
                          </Stack>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="body2" color="text.secondary">
                              Would service again: {review.would_service_again ? 'Yes' : 'No'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Recommended duration: {review.recommended_service_duration} min
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(review.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Card>
                </Grid>
              )}

              {/* Service Updates */}
              <Grid size={12}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Service Updates</Typography>
                  <Box sx={{ height: '100%', minHeight: 400 }}>
                    <ServiceUpdatesChat
                      updates={serviceUpdates}
                      currentUserId={user?.id}
                      ownerName={selectedServiceDetails?.owner ? `${selectedServiceDetails.owner.first_name} ${selectedServiceDetails.owner.last_name}` : 'Owner'}
                      handlerName={selectedServiceDetails?.handler ? `${selectedServiceDetails.handler.first_name} ${selectedServiceDetails.handler.last_name}` : 'Handler'}
                              />
                            </Box>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseServiceDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Service Dialog */}
      <Dialog
        open={manageServiceDialogOpen}
        onClose={() => setManageServiceDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Manage Service: {(() => {
                if (!activeService?.service_type) {
                  return 'Service';
                }
                if (typeof activeService.service_type === 'string') {
                  return activeService.service_type;
                }
                return activeService.service_type.name || 'Service';
              })()} for {activeService?.pets?.map(pet => pet.name).join(', ') || activeService?.pet_names?.join(', ') || 'your pets'}
            </Typography>
            <IconButton onClick={() => setManageServiceDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {/* Service Info and Map */}
              <Grid size={8}>
                <Card sx={{ p: 2, mb: 2, height: 'fit-content' }}>
                  <Typography variant="h6" gutterBottom>Service Information</Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Handler</Typography>
                      <Typography variant="body1">
                        {activeService?.handler ? `${activeService.handler.first_name || ''} ${activeService.handler.last_name || ''}`.trim() || 'Handler' : 'Handler'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip 
                        label="In Progress" 
                        color="primary" 
                        size="small" 
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                      <Typography variant="body1">
                        {activeService?.scheduled_date} at {activeService?.scheduled_time}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">{activeService?.duration} minutes</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Service Type</Typography>
                      <Typography variant="body1">
                        {activeService?.service_type?.name || 'Pet Service'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Pets</Typography>
                      <Typography variant="body1">
                        {activeService?.pets?.map(pet => pet.name).join(', ') || 'Your pets'}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="body2" color="text.secondary">Pickup Location</Typography>
                      <Typography variant="body1">
                        {activeService?.pickup_address || 'Location not specified'}
                      </Typography>
                    </Grid>
                    {activeService?.special_instructions && (
                      <Grid size={12}>
                        <Typography variant="body2" color="text.secondary">Special Instructions</Typography>
                        <Typography variant="body1">
                          {activeService.special_instructions}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Card>
              </Grid>

              {/* Map Section */}
              <Grid size={4}>
                <Card sx={{ p: 2, mb: 2, height: 'fit-content' }}>
                  <Typography variant="h6" gutterBottom>Track Handler</Typography>
                  <Box 
                    sx={{ 
                      width: '100%', 
                      height: 200, 
                      background: 'linear-gradient(135deg, #87CEEB 0%, #98FB98 50%, #F0E68C 100%)',
                      borderRadius: 1,
                      position: 'relative',
                      overflow: 'hidden',
                      border: '1px solid #ccc'
                    }}
                  >
                    {/* Map grid lines */}
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `
                        linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                      `,
                      backgroundSize: '20px 20px'
                    }} />
                    
                    {/* Roads */}
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: 0,
                      right: 0,
                      height: '8px',
                      background: '#696969',
                      transform: 'translateY(-50%)'
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: '50%',
                      width: '8px',
                      background: '#696969',
                      transform: 'translateX(-50%)'
                    }} />
                    
                    {/* Dog being walked marker */}
                    <Box sx={{
                      position: 'absolute',
                      top: '60%',
                      left: '30%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10
                    }}>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        {/* Dog icon */}
                        <Box sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: '#FF6B6B',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                          border: '2px solid white'
                        }}>
                          <Pets sx={{ fontSize: 18, color: 'white' }} />
                        </Box>
                        
                        {/* Handler icon */}
                        <Box sx={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: '#4ECDC4',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                          border: '2px solid white'
                        }}>
                          <Person sx={{ fontSize: 14, color: 'white' }} />
                        </Box>
                        
                        {/* Leash line */}
                        <Box sx={{
                          width: '2px',
                          height: '12px',
                          background: '#8B4513',
                          borderRadius: '1px'
                        }} />
                      </Box>
                    </Box>
                    
                    {/* Location label */}
                    <Box sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      background: 'rgba(255,255,255,0.9)',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      color: '#333'
                    }}>
                      🐕 Walking in progress
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Last updated: Just now
                  </Typography>
                </Card>
              </Grid>

              {/* Live handler location (while the service is in progress) */}
              {activeService?.id && activeService?.status === 'in_progress' && (
                <Grid size={12}>
                  <LiveTracking bookingId={activeService.id} isHandler={false} />
                </Grid>
              )}

              {/* Service Updates */}
              <Grid size={12}>
                <Card sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>Service Updates</Typography>
                  <Box sx={{ height: '100%', minHeight: 300 }}>
                    <ServiceUpdatesChat
                      updates={serviceUpdates}
                      currentUserId={user?.id}
                      ownerName={activeService?.owner ? `${activeService.owner.first_name} ${activeService.owner.last_name}` : 'Owner'}
                      handlerName={activeService?.handler ? `${activeService.handler.first_name} ${activeService.handler.last_name}` : 'Handler'}
                              />
                            </Box>
                </Card>
              </Grid>

              {/* Send Message */}
              <Grid size={12}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Send Message to Handler</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Send a message to your handler..."
                    value={updateMessage}
                    onChange={(e) => setUpdateMessage(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={actionLoading || !updateMessage.trim()}
                    startIcon={<Send />}
                    sx={{ textTransform: 'none' }}
                  >
                    {actionLoading ? <CircularProgress size={20} /> : 'Send Message'}
                  </Button>
                  <Button
                    component="label"
                    variant="outlined"
                    disabled={actionLoading}
                    startIcon={<PhotoCamera />}
                    sx={{ ml: 1, textTransform: 'none' }}
                  >
                    Photo
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files[0];
                        e.target.value = '';
                        if (f) handleSendPhoto(f);
                      }}
                    />
                  </Button>
                </Card>
              </Grid>

              {/* Review Section for Active Service */}
              <Grid size={12}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Your Review</Typography>
                  {activeService?.handler_review ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        {formatRating(activeService.handler_review.rating)}
                      </Box>
                      {activeService.handler_review.comment && (
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                            "{activeService.handler_review.comment}"
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Reviewed on {new Date(activeService.handler_review.created_at).toLocaleDateString()}
                      </Typography>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No review yet
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={async () => await handleOpenReviewDialog(activeService)}
                        startIcon={<Star />}
                        sx={{ textTransform: 'none' }}
                      >
                        Review Service
                      </Button>
                    </Box>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={handleCloseReviewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Review Service</Typography>
            <IconButton onClick={handleCloseReviewDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star 
                key={star}
                sx={{ 
                  fontSize: 24, 
                  color: reviewData.rating >= star ? 'gold' : 'grey.400', 
                  cursor: 'pointer',
                  transition: 'color 0.2s ease'
                }} 
                onClick={() => setReviewData({ ...reviewData, rating: star })}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Write your review (optional)"
            value={reviewData.comment}
            onChange={(e) => setReviewData({ ...reviewData, comment: e.target.value })}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReviewDialog} disabled={reviewLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmitReview} 
            variant="contained" 
            disabled={reviewLoading || reviewData.rating === 0}
            startIcon={<CheckCircle />}
          >
            {reviewLoading ? <CircularProgress size={20} /> : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OwnerDashboard; 